import * as React from "react"
import { CartStructure, Product } from "../../interfaces/SaleorTypes"
import { Col, Container, Row, Visible } from "react-grid-system"
import Card from "../../atoms/Card"
import H1 from "../../atoms/typography/H1"
import styled from "styled-components"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import BodyLarge from "../../atoms/typography/BodyLarge"
import AttributeIcon, { RulerIcon } from "../../store/AttributeIcon"
import Body from "../../atoms/typography/Body"
import H3 from "../../atoms/typography/H3"
import { theme } from "../../theme"
import { getFormattedPrice, parseDescription } from "../../../utils/cart-helpers"
import { ScaleIcon } from "../../atoms/icons/ScaleIcon"
import { CheckCircleIcon } from "../../atoms/icons/CheckCircleIcon"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import { AddShoppingCartIcon } from "../../atoms/icons/AddShoppingCartIcon"
import Counter from "../../molecules/Counter"
import { MediaQuery } from "../../../utils/design-helpers"

interface Props {
  product: Product
  addToCart: (products: CartStructure[]) => void
  hideAddToCart: boolean
  onClickAddItem?: (item: Product, amount: number) => void
}

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  max-height: 400px;
  align-items: center;
  justify-content: center;
`

const PaddedCard = styled(Card)`
  padding: 32px 42px;
`

const TextSection = styled.div`
  height: 100%;
  display: flex;
  justify-content: space-between;
  flex-direction: column;
`

const AddToCartButton = styled.div`
  width: 40%;

  ${MediaQuery.XS} {
    width: 100%;
  }
`

const AccessoriesCard: React.FC<Props> = ({ product, addToCart, hideAddToCart, onClickAddItem }) => {
  const [addedtoCart, setAddedToCart] = React.useState(false)

  const [amount, setAmount] = React.useState(1)

  const onClickAddToCart = () => {
    if (onClickAddItem !== undefined) {
      onClickAddItem(product, amount)
    } else {
      addToCart([{ product, amount: amount }])
    }
    setAddedToCart(true)
    setTimeout(() => setAddedToCart(false), 1000)
  }

  return (
    <div>
      <Container>
        <PaddedCard>
          <HorizontalSpacer spacing={1} />
          <Row>
            <Col lg={5}>
              <div style={{ position: "relative" }}>
                <Image src={product?.media[0]?.url} alt="Satcube accessories image" />
              </div>
              <HorizontalSpacer spacing={2} />
            </Col>
            <Col lg={7} md={7} sm={12} xs={12}>
              <TextSection>
                <div>
                  <H1>{product?.name}</H1>
                  <HorizontalSpacer spacing={1} />
                  <Row>
                    <Col lg={4}>
                      {product?.metadata && product?.metadata.length > 0 && (
                        <div>
                          {product?.metadata.map(
                            (attribute, index) =>
                              attribute.key !== "description" &&
                              attribute.key !== "weight" &&
                              attribute.key !== "dimensions" && (
                                <Row key={index} style={{ marginBottom: 16 }} align="center">
                                  <Col lg={1} md={1} xs={1}>
                                    <AttributeIcon icon={attribute.key} />
                                  </Col>
                                  <Spacer spacing={1} />
                                  <Col lg={9} md={10} xs={10}>
                                    <BodyLarge>{attribute.value}</BodyLarge>
                                  </Col>
                                </Row>
                              ),
                          )}
                        </div>
                      )}
                    </Col>
                    <Col lg={6}>
                      <BodyLarge>{parseDescription(product.description)}</BodyLarge>
                      <HorizontalSpacer spacing={0.25} />
                      <Row align="center">
                        <Col md={1}>
                          <RulerIcon />
                        </Col>
                        <Col md={11}>
                          <Body>{product?.metadata?.find((x) => x.key === "dimensions")?.value}</Body>
                        </Col>
                      </Row>
                      <HorizontalSpacer spacing={0.25} />
                      <Row align="center">
                        <Col md={1}>
                          <ScaleIcon />
                        </Col>
                        <Col md={11}>
                          <Body>{product?.metadata?.find((x) => x.key === "weight")?.value}</Body>
                        </Col>
                      </Row>
                      <HorizontalSpacer spacing={0.25} />
                      <Body>{product?.metadata?.find((x) => x.key === "description")?.value}</Body>
                    </Col>
                    <Col lg={2} />
                  </Row>
                </div>
                {hideAddToCart === false && (
                  <div>
                    <Row justify="between" style={{ padding: "1rem", minHeight: 80 }} align="end">
                      <Body>
                        Price:{" "}
                        {getFormattedPrice(
                          product.variants[0]?.pricing.price.gross.amount,
                          product.variants[0]?.pricing.price.currency,
                        )}
                      </Body>
                      <div>
                        <Counter
                          currentValue={amount}
                          onClickAdd={() => setAmount(amount + 1)}
                          onClickMinus={() => setAmount(amount - 1)}
                          minValue={1}
                        />
                      </div>

                      <AddToCartButton>
                        {addedtoCart ? (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <CheckCircleIcon />
                            <Spacer spacing={1} />
                            <H3 color={theme.colors.greenDark}>Added to cart</H3>
                          </div>
                        ) : (
                          <div>
                            <Visible xs sm md>
                              <HorizontalSpacer spacing={2} />
                            </Visible>
                            <SecondaryButton
                              title="Add to cart"
                              onClick={onClickAddToCart}
                              icon={<AddShoppingCartIcon color="white" />}
                              id={`add-to-cart-${product?.name.toLowerCase().replace(/\s+/g, "-")}`}
                            />
                          </div>
                        )}
                      </AddToCartButton>
                    </Row>
                  </div>
                )}
              </TextSection>
            </Col>
          </Row>
        </PaddedCard>
      </Container>
    </div>
  )
}

export default AccessoriesCard
